import React, { useEffect, useState } from 'react';
import { Button, Modal, notification, Typography } from 'antd';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { EditFilled, HistoryOutlined, LinkOutlined } from '@ant-design/icons';

import { StyledTable } from '../../common/tableView.styles';
import { AddIcon } from '../../components/TableWithDataUpdate';
import { usePost } from '../../utils/reactQuery';
import { pathToUrl } from '../../utils/router';
import { ApiRoutesEnum } from '../../utils/routes';
import { CandidateType } from '../candidates/Candidates.types';

import { AddCandidateConsentsForm, AddCandidateConsentsFormValues } from './AddCandidateConsentsForm';
import { CandidateConsentHistory } from './CandidateConsentHistory';
import { CandidateConsentsLink } from './CandidateConsentsLink';
import { CandidateConsent, CandidateConsentStatus, ConsentType, consentTypeMap } from './Consents.types';
import { EditCandidateConsentForm } from './EditCandidateConsentForm';
import { FormattedCandidateConsentStatus } from './FormattedCandidateConsentStatus';

const StyledDefaultHeader = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 576px) {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
  }
`;

const StyledDefaultHeaderButtons = styled.div`
  display: flex;
  gap: 5px;
  justify-content: flex-end;
`;

const StyledActionButton = styled(Button)`
  color: #bdbdbd;
  border: 0;

  :hover {
    color: black !important;
    background-color: #f5f5f5 !important;
  }
`;

type CandidateConsentsTableMode =
  | { name: 'default' }
  | { name: 'adding'; initialConsentTypes?: ConsentType[] }
  | { name: 'editing'; consentId: number };

export type CandidateConsentsAddIntent = {
  id: number;
  consentTypes: ConsentType[];
};

export const CandidateConsentsTable: React.FC<{
  candidateId: string;
  consents: CandidateConsent[];
  consentsLink: CandidateType['activeCandidateTokenLink'];
  email: CandidateType['email'];
  addIntent?: CandidateConsentsAddIntent | null;
  onConsentChange?: () => void;
}> = ({ candidateId, consents, consentsLink, email, addIntent, onConsentChange }) => {
  const [previewedConsentId, setPreviewedConsentId] = useState<number | null>(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const [mode, setMode] = useState<CandidateConsentsTableMode>({ name: 'default' });

  const queryClient = useQueryClient();
  const invalidateCandidateQuery = () =>
    queryClient.invalidateQueries(pathToUrl(ApiRoutesEnum.getCandidate, { id: candidateId }));

  useEffect(() => {
    if (!addIntent) {
      return;
    }

    setMode({ name: 'adding', initialConsentTypes: addIntent.consentTypes });
  }, [addIntent]);

  const { mutate: addConsents } = usePost({ url: ApiRoutesEnum.addCandidateConsents });
  const onAdd = ({ consentTypes, clients, projects }: AddCandidateConsentsFormValues) => {
    const clientDataProcessingConsents =
      consentTypes.includes('CLIENT_DATA_PROCESSING') && clients
        ? clients.map((clientUuid) => ({
            type: 'CLIENT_DATA_PROCESSING',
            candidateUuid: candidateId,
            clientUuid,
          }))
        : [];

    const projectParticipationConsents =
      consentTypes?.includes('PROJECT_PARTICIPATION') && projects
        ? projects.map((projectUuid) => ({
            type: 'PROJECT_PARTICIPATION',
            candidateUuid: candidateId,
            projectUuid,
          }))
        : [];

    const data = [
      ...(consentTypes?.includes('DATA_PROCESSING_AND_OFFERS')
        ? [{ type: 'DATA_PROCESSING_AND_OFFERS', candidateUuid: candidateId }]
        : []),
      ...clientDataProcessingConsents,
      ...projectParticipationConsents,
    ];

    if (data.length > 0) {
      addConsents(
        { data },
        {
          onSuccess: () => {
            setMode({ name: 'default' });
            invalidateCandidateQuery();
            onConsentChange?.();
          },
          onError: (err) => {
            console.error(err);
            notification.error({
              message: 'Error',
              description: parseErrorMessage(err.response?.data.messages),
            });
          },
        }
      );
    }
  };

  const { mutate: updateConsent } = usePost({ url: ApiRoutesEnum.updateCandidateConsents });
  const onEdit = ({ consentId, status }: { consentId: number; status: CandidateConsentStatus }) => {
    const operationType = operationTypesByStatus[status];
    if (operationType) {
      updateConsent(
        { data: [{ consentId, operationType }] },
        {
          onSuccess: () => {
            setMode({ name: 'default' });
            invalidateCandidateQuery();
            onConsentChange?.();
          },
          onError: (err) => {
            console.error(err);
            notification.error({
              message: 'Error',
              description: parseErrorMessage(err.response?.data.messages),
            });
          },
        }
      );
    }
  };

  return (
    <>
      <div style={{ marginBottom: '15px' }}>
        {mode.name === 'adding' && (
          <AddCandidateConsentsForm
            initialConsentTypes={mode.initialConsentTypes}
            onSubmit={onAdd}
            onCancel={() => setMode({ name: 'default' })}
          />
        )}

        {mode.name === 'editing' && (
          <EditCandidateConsentForm
            consent={consents.find(({ id }) => id === mode.consentId)}
            onSubmit={(values) => onEdit({ consentId: mode.consentId, status: values.status })}
            onCancel={() => setMode({ name: 'default' })}
          />
        )}

        {mode.name === 'default' && (
          <StyledDefaultHeader>
            <Typography.Title level={5} style={{ paddingLeft: '15px' }}>
              Consents
            </Typography.Title>

            <StyledDefaultHeaderButtons>
              <Button icon={<LinkOutlined />} onClick={() => setIsLinkModalOpen(true)}>
                Link to consents
              </Button>
              <Button icon={<AddIcon />} onClick={() => setMode({ name: 'adding' })}>
                Add consents
              </Button>
            </StyledDefaultHeaderButtons>
          </StyledDefaultHeader>
        )}
      </div>

      <StyledTable
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Consent type',
            dataIndex: 'type',
            render: (consentType: ConsentType) => consentTypeMap[consentType],
            sorter: (a: CandidateConsent, b: CandidateConsent) => a.type.localeCompare(b.type),
          },
          {
            title: 'Related entity',
            width: 150,
            render: (candidateConsent: CandidateConsent) => (
              <>
                {candidateConsent.client && (
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    {candidateConsent.client.name}{' '}
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                      (Client)
                    </Typography.Text>
                  </span>
                )}
                {candidateConsent.project && (
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    {candidateConsent.project.name}{' '}
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                      (Project)
                    </Typography.Text>
                  </span>
                )}
              </>
            ),
            sorter: (a: CandidateConsent, b: CandidateConsent) => {
              // NOTE: Assuming `client` and `project` are mutually exclusive
              const aEntity = `${a.client?.name ?? ''}${a.project?.name ?? ''}`;
              const bEntity = `${b.client?.name ?? ''}${b.project?.name ?? ''}`;
              return aEntity.localeCompare(bEntity);
            },
          },
          {
            title: 'Responsible',
            dataIndex: 'createdBy',
            width: 175,
            render: (createdBy: CandidateConsent['createdBy']) =>
              createdBy ? `${createdBy.firstName} ${createdBy.lastName}` : '',
            sorter: (a: CandidateConsent, b: CandidateConsent) => {
              const aName = `${a.createdBy?.firstName ?? ''} ${a.createdBy?.lastName ?? ''}`;
              const bName = `${b.createdBy?.firstName ?? ''} ${b.createdBy?.lastName ?? ''}`;
              return aName.localeCompare(bName);
            },
          },
          {
            title: 'Status update date',
            dataIndex: 'createdDate',
            width: 175,
            render: (createdDate: string) => dayjs(createdDate).format('DD-MM-YYYY'),
            sorter: (a: CandidateConsent, b: CandidateConsent) => a.createdDate.localeCompare(b.createdDate),
          },
          {
            title: 'Expiry date',
            dataIndex: 'expiryDate',
            width: 150,
            render: (expiryDate: string | null) => expiryDate && dayjs(expiryDate).format('DD-MM-YYYY'),
            sorter: (a: CandidateConsent, b: CandidateConsent) => {
              const aExpiryDate = a.expiryDate ?? '';
              const bExpiryDate = b.expiryDate ?? '';
              return aExpiryDate.localeCompare(bExpiryDate);
            },
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 150,
            render: (status: CandidateConsentStatus) => <FormattedCandidateConsentStatus status={status} />,
            sorter: (a: CandidateConsent, b: CandidateConsent) => a.status.localeCompare(b.status),
          },
          {
            dataIndex: 'id',
            render: (consentId: number) => {
              return (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                  <StyledActionButton
                    shape="circle"
                    icon={<HistoryOutlined />}
                    onClick={() => setPreviewedConsentId(consentId)}
                  />

                  <StyledActionButton
                    style={{ border: 'none' }}
                    shape="circle"
                    icon={<EditFilled />}
                    onClick={() => setMode({ name: 'editing', consentId })}
                  />
                </div>
              );
            },
          },
        ]}
        dataSource={consents}
      />

      <Modal
        destroyOnClose
        open={!!previewedConsentId}
        onCancel={() => setPreviewedConsentId(null)}
        title="Consent history"
        footer={
          <Button type="primary" onClick={() => setPreviewedConsentId(null)}>
            Close
          </Button>
        }
      >
        <CandidateConsentHistory candidateConsent={consents.find(({ id }) => id === previewedConsentId)} />
      </Modal>

      <Modal
        destroyOnClose
        open={isLinkModalOpen}
        onCancel={() => setIsLinkModalOpen(false)}
        title="Link to consents"
        footer={
          <Button type="primary" onClick={() => setIsLinkModalOpen(false)}>
            Close
          </Button>
        }
      >
        <CandidateConsentsLink candidateId={candidateId} link={consentsLink} email={email} />
      </Modal>
    </>
  );
};

const operationTypesByStatus: Partial<Record<CandidateConsentStatus, string>> = {
  APPROVED: 'APPROVE',
  RENEWED: 'RENEW',
  REVOKED: 'REVOKE',
};

const errorMessagesByCode: Record<string, string | undefined> = {
  'requested-not-newest': 'Cannot update: a newer consent of this type exists',
  'exists-with-last-template-version': 'Cannot add: a current consent of this type already exists',
};

const parseErrorMessage = (messages?: Record<string, string>): string => {
  const messageValues = messages && Object.values(messages);

  if (!messageValues || messageValues.length === 0) {
    return 'An unknown error has occurred';
  }

  const errorCode = messageValues[0];
  const errorMessage = errorCode && errorMessagesByCode[errorCode];
  return errorMessage || 'An unknown error has occurred';
};
